// src/components/profile/modals/SubscriptionModal.jsx
import React, { useState, useEffect } from 'react';
import { Spinner } from 'react-bootstrap';
import { fetchSubscriptionPlans, calculatePlanUpgradeAmount } from '../../../services/profile.service';
import useModalHistory from '../../../hooks/useModalHistory';
import { CONTENT_ENDPOINTS } from '../../../utils/constants';
import '../../../styles/components/modals.css';

const SubscriptionModal = ({ show, onHide, token, currentSubscriptionType, onSelectPlan }) => {
  const [plans, setPlans] = useState([]);
  const [originalPlans, setOriginalPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [country, setCountry] = useState(null);
  const [locationLoading, setLocationLoading] = useState(true);

  useModalHistory(show, onHide, 'subscription-modal');

  // Load subscription plans when modal opens
  useEffect(() => {
    if (show) {
      // Debug the props being passed

      loadSubscriptionPlans();
    }
  }, [show, currentSubscriptionType]);

  useEffect(() => {
    const detectLocation = async () => {
      try {
        setLocationLoading(true);
        const response = await fetch('https://ipapi.co/json/');
        const data = await response.json();
        setCountry(data.country_code);
      } catch (error) {
        console.error('Error detecting location:', error);
        setCountry('UNKNOWN');
      } finally {
        setLocationLoading(false);
      }
    };

    if (show) {
      detectLocation();
    }
  }, [show]);

  // Load subscription plans
  // Load subscription plans
  const loadSubscriptionPlans = async () => {
    setLoading(true);
    setError(null);

    try {
      const plansData = await fetchSubscriptionPlans(token);

      // Store original plans for calculations
      const allPlans = plansData.filter(plan => plan.id !== 3); // Filter out trial plan
      setOriginalPlans(allPlans); // Store original plans

      // Filter plans based on current subscription for display
      let filteredPlans = [...allPlans]; // Copy the array

      // If currentSubscriptionType is undefined, show all plans
      if (!currentSubscriptionType) {
        filteredPlans.sort((a, b) => parseFloat(a.Price) - parseFloat(b.Price));
        setPlans(filteredPlans);
        return;
      }

      // Convert currentSubscriptionType to number for reliable comparison
      const currentPlan = parseInt(currentSubscriptionType);

      // Additional filtering based on current subscription
      if (currentPlan === 1) {
        // Basic users should only see Plus plan
        filteredPlans = filteredPlans.filter(plan => plan.id === 2);
      } else if (currentPlan === 2) {
        // Plus users should only see Basic plan for downgrade
        filteredPlans = filteredPlans.filter(plan => plan.id === 1);
      } else if (currentPlan === 3) {
        // Trial users see both plans
      }
      filteredPlans.sort((a, b) => parseFloat(a.Price) - parseFloat(b.Price));

      setPlans(filteredPlans);
    } catch (error) {
      console.error('Error fetching subscription plans:', error);
      setError('Failed to load subscription plans. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Handle plan selection
  const handlePlanSelection = async (planId) => {
    try {
      // Find the selected plan
      const selectedPlan = plans.find(plan => plan.id === parseInt(planId));

      if (!selectedPlan) {
        throw new Error('Selected plan not found');
      }

      // Check if this is a downgrade from Pro to Basic
      if (currentSubscriptionType === '2' && planId === '1') {
        // Check for legacy items - Implement this based on your requirements
        try {
          const inheritanceCheck = await checkForInheritanceItems(token);

          if (inheritanceCheck && inheritanceCheck.length > 0) {
            alert('Cannot downgrade plan. You have active Endowment items in your account. Please delete them before downgrading.');
            return;
          }
        } catch (error) {
          console.error('Error checking inheritance items:', error);
          alert('Unable to verify Endowment items. Please try again later.');
          return;
        }
      }

      // Get account details for proration calculation if upgrading
      const accountDetails = JSON.parse(sessionStorage.getItem('accountDetails'));

      // For upgrades from Basic to Plus, calculate prorated amount
      const currentPlan = parseInt(currentSubscriptionType);
      const newPlan = parseInt(planId);

      if (currentPlan === 1 && newPlan === 2 && accountDetails?.payment?.NextPayDate) {
        try {
          const calculatedUpgradeDetails = await calculatePlanUpgradeAmount(
            currentSubscriptionType,
            selectedPlan,
            accountDetails.payment.NextPayDate,
            token
          );

          // Store the calculation in sessionStorage
          sessionStorage.setItem('calculatedUpgradeDetails', JSON.stringify(calculatedUpgradeDetails));

          // Also calculate and store Rand amounts
          const randPrices = { 1: 100, 2: 370 };
          const originalRandAmount = randPrices[newPlan];
          const dailyRandRate = randPrices[1] / 365; // Basic plan daily rate
          const remainingRandCredit = dailyRandRate * calculatedUpgradeDetails.calculationDetails.remainingDays;
          const proratedRandAmount = Math.max(0, originalRandAmount - remainingRandCredit);

          // Update the stored calculation with Rand amounts
          const updatedCalculation = {
            ...calculatedUpgradeDetails,
            randCalculations: {
              originalAmount: originalRandAmount,
              dailyRate: dailyRandRate,
              remainingCredit: remainingRandCredit,
              proratedAmount: proratedRandAmount
            }
          };

          sessionStorage.setItem('calculatedUpgradeDetails', JSON.stringify(updatedCalculation));

        } catch (error) {
          console.error('Error calculating prorated amount:', error);
          // Continue without proration if calculation fails
        }
      }

      // Store plan details
      sessionStorage.setItem('selectedPlanId', planId);
      sessionStorage.setItem('selectedPlanDetails', JSON.stringify(selectedPlan));

      // Call the onSelectPlan callback with the selected plan
      if (onSelectPlan) {
        onSelectPlan(selectedPlan);
      }

      // Close the modal
      onHide();
    } catch (error) {
      console.error('Error handling plan selection:', error);
      alert('Error selecting plan. Please try again.');
    }
  };

  // Check for inheritance items (placeholder function)
  const checkForInheritanceItems = async (token) => {
    try {
      const response = await fetch(CONTENT_ENDPOINTS.BUCKET_CONTENT, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ bucket: 'inheritance' })
      });

      if (!response.ok) {
        throw new Error('Failed to fetch endowment data');
      }

      const result = await response.json();
      return result.filesMetadata || [];
    } catch (error) {
      console.error('Error fetching inheritances:', error);
      throw error;
    }
  };

  // Calculate prorated upgrade amount (ported from JS file)
  const calculatePlanUpgradeAmount = async (currentPlanId, newPlan, nextPayDate) => {


    // Find current plan details from the plans array
    const currentPlan = originalPlans.find(plan => plan.id === parseInt(currentPlanId));
    if (!currentPlan) {
      console.error("Current plan not found in subscription plans");
      throw new Error("Current plan not found in subscription plans");
    }

    // Convert prices from strings to numbers
    const currentPlanPrice = parseFloat(currentPlan.Price);
    const newPlanPrice = parseFloat(newPlan.Price);

    // Convert dates and normalize to start of day
    const nextPaymentDate = new Date(nextPayDate);
    nextPaymentDate.setHours(0, 0, 0, 0);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Calculate remaining days in current subscription
    const totalDays = 365; // Annual subscription
    const daysUsed = Math.floor((today - new Date(nextPaymentDate).setFullYear(nextPaymentDate.getFullYear() - 1)) / (1000 * 60 * 60 * 24));
    const remainingDays = Math.max(0, totalDays - daysUsed);

    // Calculate daily rates and credit
    const currentPlanDailyRate = currentPlanPrice / 365;
    const credit = currentPlanDailyRate * remainingDays;

    // Calculate final subsidized amount
    const subsidizedAmount = Math.max(0, newPlanPrice - credit);

    // Set new next pay date
    const newNextPayDate = new Date(today);
    newNextPayDate.setFullYear(today.getFullYear() + 1);

    const result = {
      originalAmount: newPlanPrice,
      subsidizedAmount: Math.round(subsidizedAmount * 100) / 100,
      discount: Math.round(credit * 100) / 100,
      newNextPayDate: newNextPayDate.toISOString(),
      calculationDetails: {
        totalDays,
        daysUsed,
        remainingDays,
        currentPlanDailyRate: Math.round(currentPlanDailyRate * 100) / 100,
        creditAmount: Math.round(credit * 100) / 100
      }
    };

    return result;
  };

  // Get plan pricing display (regular or prorated)
  const getPlanPricing = (plan) => {
    const currentPlan = parseInt(currentSubscriptionType);
    const planId = parseInt(plan.id);

    // Check if this is an upgrade from Basic to Plus
    const isUpgrade = currentPlan === 1 && planId === 2;

    if (isUpgrade) {
      // For upgrades, we need to calculate and display both USD and Rand prorated amounts
      const accountDetails = JSON.parse(sessionStorage.getItem('accountDetails'));

      if (accountDetails?.payment?.NextPayDate) {
        // Calculate remaining days (existing logic)
        const nextPaymentDate = new Date(accountDetails.payment.NextPayDate);
        const today = new Date();

        const oneYearAgo = new Date(nextPaymentDate);
        oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);

        const totalDays = 365;
        const daysUsed = Math.floor((today - oneYearAgo) / (1000 * 60 * 60 * 24));
        const remainingDays = Math.max(0, totalDays - daysUsed);

        if (remainingDays > 0) {
          // Calculate USD proration
          const currentPlanDetails = originalPlans.find(p => p.id === currentPlan);
const currentPlanPrice = currentPlanDetails ? parseFloat(currentPlanDetails.Price) : 5.99;
          const newPlanPrice = parseFloat(plan.Price);
          const dailyUSDRate = currentPlanPrice / 365;
          const usdCredit = dailyUSDRate * remainingDays;
          const proratedUSDAmount = Math.max(0, newPlanPrice - usdCredit);

          // Calculate Rand proration
          const currentRandPrice = 100; // Basic plan price in Rand
          const newRandPrice = randPrices[planId];
          const dailyRandRate = currentRandPrice / 365;
          const randCredit = dailyRandRate * remainingDays;
          const proratedRandAmount = Math.max(0, newRandPrice - randCredit);

          // Show currency based on location
          if (country === 'ZA') {
            return (
              <>
                <div style={{ textDecoration: 'line-through', fontSize: '0.9em', color: '#666' }}>
                  R{randPrices[planId].toFixed(2)}
                </div>
                <div style={{ color: '#28a745', fontWeight: 'bold' }}>
                  R{proratedRandAmount.toFixed(2)}
                </div>
                <div style={{ fontSize: '0.8em', color: '#17a2b8' }}>
                  Prorated ({remainingDays} days remaining)
                </div>
              </>
            );
          } else {
            return (
              <>
                <div style={{ textDecoration: 'line-through', fontSize: '0.9em', color: '#666' }}>
                  ${parseFloat(plan.Price).toFixed(2)}
                </div>
                <div style={{ color: '#28a745', fontWeight: 'bold' }}>
                  ${proratedUSDAmount.toFixed(2)}
                </div>
                <div style={{ fontSize: '0.8em', color: '#17a2b8' }}>
                  Prorated ({remainingDays} days remaining)
                </div>
              </>
            );
          }
        }
      }
    }

    // Regular pricing for non-upgrades or when no proration applies
    if (country === 'ZA') {
      return `R${randPrices[plan.id].toFixed(2)}`;
    } else {
      return `$${parseFloat(plan.Price).toFixed(2)}`;
    }
  };

  // Get button text based on context
  const getButtonText = (planId) => {
    if (!currentSubscriptionType) {
      return 'Select Plan'; // Default when subscription type is unknown
    }

    const currentPlan = parseInt(currentSubscriptionType);

    if (currentPlan === 3) {
      return planId === 2 ? 'Upgrade to Plus' : 'Choose Basic';
    }

    if (currentPlan === 1 && planId === 2) {
      return 'Upgrade to Plus';
    }

    if (currentPlan === 2 && planId === 1) {
      return 'Downgrade to Basic';
    }

    return 'Select Plan';
  };

  // Determine if plan is currently selected
  const isPlanSelected = (planId) => {
    if (!currentSubscriptionType) return false;
    return parseInt(currentSubscriptionType) === parseInt(planId);
  };

  // Determine if button should be shown - simplified since we filter plans now
  const shouldShowButton = (planId) => {
    // Don't show button if user already has this plan
    return !isPlanSelected(planId);
  };

  // Hard-coded Rand prices
  const randPrices = {
    1: 100, // Basic plan price in Rand
    2: 370  // Plus plan price in Rand
  };

  if (!show) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content plan-modal">
        <div className="modal-header">
          <h5 className="modal-title">Choose Your Plan</h5>
          <button type="button" className="close-button" onClick={onHide}>×</button>
        </div>

        <div className="modal-body">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="success" />
              <p className="mt-3">Loading subscription plans...</p>
            </div>
          ) : error ? (
            <div className="alert alert-danger">
              <i className="fas fa-exclamation-triangle mr-2"></i>
              {error}
            </div>
          ) : (
            <div className={`plan-cards-grid ${plans.length === 1 ? 'single-plan' : ''}`}>
              {plans.length === 1 ? (
                // Single plan - center it
                <div className="plan-cards-column single-plan-column">
                  {plans.map((plan) => (
                    <div key={plan.id} className="plan-card">
                      <div className={`card-content ${plan.id === 2 ? 'premium-plan' : ''}`}>
                        {plan.id === 2 && <div className="popular-badge">Most Popular</div>}

                        {/* Current Plan Badge */}
                        {isPlanSelected(plan.id) && (
                          <div className="current-plan-badge">Current Plan</div>
                        )}


                        <h5 className="card-title">{plan.Name}</h5>
                        <h6 className="card-subtitle">
                          {getPlanPricing(plan)}
                          <span className="pricing-period">per year</span>
                        </h6>
                        <p className="card-description">{plan.Description}</p>

                        <ul className="feature-list">
                          <li>✓ {plan.Feature1}</li>
                          <li>✓ {plan.Feature2}</li>
                          <li>✓ {plan.Feature3}</li>
                        </ul>

                        {shouldShowButton(plan.id) && (
                          <button
                            className={`select-plan-btn ${isPlanSelected(plan.id) ? 'selected' : ''}`}
                            onClick={() => handlePlanSelection(plan.id)}
                            disabled={isPlanSelected(plan.id)}
                          >
                            {isPlanSelected(plan.id) ? 'Current Plan' : getButtonText(plan.id)}
                          </button>
                        )}

                        {!shouldShowButton(plan.id) && isPlanSelected(plan.id) && (
                          <div className="current-plan-indicator">
                            <i className="fas fa-check-circle"></i>
                            Your Current Plan
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                // Multiple plans - use two columns
                <>
                  <div className="plan-cards-column">
                    {plans.filter((_, index) => index % 2 === 0).map((plan) => (
                      <div key={plan.id} className="plan-card">
                        <div className={`card-content ${plan.id === 2 ? 'premium-plan' : ''}`}>
                          {plan.id === 2 && <div className="popular-badge">Most Popular</div>}

                          {/* Current Plan Badge */}
                          {isPlanSelected(plan.id) && (
                            <div className="current-plan-badge">Current Plan</div>
                          )}


                          <h5 className="card-title">{plan.Name}</h5>
                          <h6 className="card-subtitle">
                            ${parseFloat(plan.Price).toFixed(2)} / R{randPrices[plan.id].toFixed(2)}
                            <span className="pricing-period">per year</span>
                          </h6>
                          <p className="card-description">{plan.Description}</p>

                          <ul className="feature-list">
                            <li>✓ {plan.Feature1}</li>
                            <li>✓ {plan.Feature2}</li>
                            <li>✓ {plan.Feature3}</li>
                          </ul>

                          {shouldShowButton(plan.id) && (
                            <button
                              className={`select-plan-btn ${isPlanSelected(plan.id) ? 'selected' : ''}`}
                              onClick={() => handlePlanSelection(plan.id)}
                              disabled={isPlanSelected(plan.id)}
                            >
                              {isPlanSelected(plan.id) ? 'Current Plan' : getButtonText(plan.id)}
                            </button>
                          )}

                          {!shouldShowButton(plan.id) && isPlanSelected(plan.id) && (
                            <div className="current-plan-indicator">
                              <i className="fas fa-check-circle"></i>
                              Your Current Plan
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="plan-cards-column">
                    {plans.filter((_, index) => index % 2 === 1).map((plan) => (
                      <div key={plan.id} className="plan-card">
                        <div className={`card-content ${plan.id === 2 ? 'premium-plan' : ''}`}>
                          {plan.id === 2 && <div className="popular-badge">Most Popular</div>}

                          {/* Current Plan Badge */}
                          {isPlanSelected(plan.id) && (
                            <div className="current-plan-badge">Current Plan</div>
                          )}


                          <h5 className="card-title">{plan.Name}</h5>
                          <h6 className="card-subtitle">
                            ${parseFloat(plan.Price).toFixed(2)} / R{randPrices[plan.id].toFixed(2)}
                            <span className="pricing-period">per year</span>
                          </h6>
                          <p className="card-description">{plan.Description}</p>

                          <ul className="feature-list">
                            <li>✓ {plan.Feature1}</li>
                            <li>✓ {plan.Feature2}</li>
                            <li>✓ {plan.Feature3}</li>
                          </ul>

                          {shouldShowButton(plan.id) && (
                            <button
                              className={`select-plan-btn ${isPlanSelected(plan.id) ? 'selected' : ''}`}
                              onClick={() => handlePlanSelection(plan.id)}
                              disabled={isPlanSelected(plan.id)}
                            >
                              {isPlanSelected(plan.id) ? 'Current Plan' : getButtonText(plan.id)}
                            </button>
                          )}

                          {!shouldShowButton(plan.id) && isPlanSelected(plan.id) && (
                            <div className="current-plan-indicator">
                              <i className="fas fa-check-circle"></i>
                              Your Current Plan
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button type="button" className="close-button-footer" onClick={onHide}>Close</button>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionModal;